<!-- src/apps/secret/components/receipt/TimelineDisplay.vue -->

<script setup lang="ts">
  import type { Receipt, ReceiptDetails } from '@/schemas/shapes/v3/receipt';
  import { useSecretExpiration } from '@/shared/composables/useSecretExpiration';
  import { formatDisplayDateTime } from '@/utils/format';
  import { formatRelativeTimeToNow } from '@/utils/format/relative-time';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  interface Props {
    record: Receipt;
    details: ReceiptDetails;
  }

  const props = defineProps<Props>();

  const { progress, timeRemaining, expirationDate, expirationState } = useSecretExpiration(
    props.record.created,
    props.record.expiration_in_seconds ?? 0
  );

  const showExpiration = computed(() => !props.record.is_burned && !props.record.is_revealed);
  const showFaded = computed(() => expirationState.value === 'expired' || !showExpiration.value);

  // Marker tone per event, kept discreet to match the terminal chrome: a small
  // filled dot for lifecycle events, a hollow ring for the pending expiry.
  type Tone = 'brand' | 'neutral' | 'received' | 'burned' | 'expiry';

  interface TimelineEvent {
    key: string;
    label: string;
    /** ISO string for the <time datetime> attr; undefined when no date. */
    datetime?: string;
    /** Localized absolute date/time; empty string when no date. */
    dateText: string;
    /** Localized relative time ("2 hours ago" / "in 7 days"); empty to hide. */
    relative: string;
    tone: Tone;
  }

  const formatTimeAgo = (date: Date) => formatRelativeTimeToNow(date, { addSuffix: true });

  const buildEvent = (
    key: string,
    label: string,
    date: Date | null | undefined,
    tone: Tone
  ): TimelineEvent => ({
    key,
    label,
    datetime: date ? date.toISOString() : undefined,
    dateText: date ? formatDisplayDateTime(date) : '',
    relative: date ? formatTimeAgo(date) : '',
    tone,
  });

  // Obscured recipient for the "sent by email" step. The backend already
  // obscures the address in safe_dump (recipients), and recipient_name is the
  // preferred display when present; recipients may arrive as a string or a
  // one-element array.
  const recipientDisplay = computed<string>(() => {
    const r = props.record;
    if (r.recipient_name) return r.recipient_name;
    const rc = r.recipients;
    if (Array.isArray(rc)) return rc[0] ?? '';
    return rc ?? '';
  });

  // Only email delivery leaves a server-side record, so only it earns a
  // timeline step. WhatsApp/Telegram are client-side hand-offs (the user's own
  // app sends the message) and produce no server event.
  const wasEmailed = computed(
    () => props.details.show_recipients || recipientDisplay.value.length > 0
  );

  const events = computed<TimelineEvent[]>(() => {
    const r = props.record;
    const list: TimelineEvent[] = [
      buildEvent('created', t('web.STATUS.created'), r.created, 'brand'),
    ];

    // "Enviado por e-mail" — right after Created. There is no dedicated
    // sent-at timestamp; in the standard flow the email is enqueued at creation,
    // so `created` is the send instant.
    if (wasEmailed.value) {
      const label = recipientDisplay.value
        ? `${t('receipt_terminal.timeline_sent_email')} — ${recipientDisplay.value}`
        : t('receipt_terminal.timeline_sent_email');
      list.push(buildEvent('sent_email', label, r.created, 'brand'));
    }

    if (r.is_previewed) {
      list.push(buildEvent('previewed', t('web.STATUS.previewed'), r.previewed, 'neutral'));
    }
    if (r.is_revealed) {
      list.push(buildEvent('received', t('web.STATUS.received'), r.revealed, 'received'));
    }
    if (r.is_burned) {
      list.push(buildEvent('burned', t('web.STATUS.burned'), r.burned, 'burned'));
    }
    if (showExpiration.value) {
      list.push({
        key: 'expiration',
        label: progress.value >= 100 ? t('web.STATUS.expired') : t('web.STATUS.expires'),
        datetime: expirationDate.value ? expirationDate.value.toISOString() : undefined,
        dateText: expirationDate.value ? formatDisplayDateTime(expirationDate.value) : '',
        // The expiry countdown is already a localized relative string.
        relative: timeRemaining.value,
        tone: 'expiry',
      });
    }

    return list;
  });

  // Marker classes per tone. "expiry" is a hollow ring; the rest are small
  // filled dots. Only "created" carries the brand glow used elsewhere on the
  // receipt (status strip, hero bullet).
  const markerClass: Record<Tone, string> = {
    brand: 'bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]',
    neutral: 'bg-day-ink-faint dark:bg-night-ink-faint',
    received: 'bg-emerald-500',
    burned: 'bg-amber-500',
    expiry:
      'bg-day dark:bg-night-100 border border-day-ink-faint/70 dark:border-night-ink-faint/70',
  };
</script>

<template>
  <!-- prettier-ignore-attribute class -->
  <div
    data-testid="receipt-timeline"
    class="relative"
    :class="{ 'opacity-70': showFaded }">
    <!-- Hairline rail the markers sit on -->
    <!-- prettier-ignore-attribute class -->
    <div
      class="absolute inset-y-2 left-[3px] w-px bg-day-line
        dark:bg-white/[0.07]"
      aria-hidden="true"></div>

    <ol class="space-y-5">
      <li
        v-for="event in events"
        :key="event.key"
        class="relative pl-6">
        <!-- Marker -->
        <span
          class="absolute top-[3px] left-0 size-[7px] rounded-full"
          :class="markerClass[event.tone]"
          aria-hidden="true"></span>

        <div class="flex items-baseline justify-between gap-3">
          <!-- prettier-ignore-attribute class -->
          <p
            data-testid="timeline-event-label"
            class="font-mono text-[10px] tracking-[0.08em] text-day-ink-muted
              uppercase dark:text-night-ink-muted">
            {{ event.label }}
          </p>
          <!-- prettier-ignore-attribute class -->
          <span
            v-if="event.relative"
            class="shrink-0 font-mono text-[10px] tracking-[0.02em]
              text-day-ink-muted dark:text-night-ink-muted">
            {{ event.relative }}
          </span>
        </div>

        <time
          :datetime="event.datetime"
          class="mt-1 block font-mono text-xs text-day-ink dark:text-night-ink">
          {{ event.dateText }}
        </time>
      </li>
    </ol>
  </div>
</template>

<style scoped></style>
